import { LogLevel } from './log-level';

/**
 * Writes a log line to the developer console.
 *
 * Absorbed from the former `ConsoleLogAdapter`. It is a plain function rather than an injectable:
 * there is one logging service now, and the console is not a pluggable destination.
 *
 * The original `Error` is passed through untouched so the runtime renders its own stack with
 * source-mapped, navigable frames. Stringifying it here would lose that, which is the single
 * reason console output is not derived from the telemetry pipeline.
 */
export function writeToConsole(
    level: LogLevel,
    domain: string,
    message: string,
    properties?: Record<string, unknown>,
    error?: Error,
): void {
    const args: unknown[] = [domain ? `[${domain}] ${message}` : message];

    if (properties && Object.keys(properties).length > 0) {
        args.push(properties);
    }
    if (error) {
        args.push(error);
    }

    switch (level) {
        case LogLevel.VERBOSE:
        case LogLevel.DEBUG:
            // eslint-disable-next-line no-console
            console.debug(...args);
            break;
        case LogLevel.INFO:
            // eslint-disable-next-line no-console
            console.info(...args);
            break;
        case LogLevel.WARNING:
            console.warn(...args);
            break;
        case LogLevel.ERROR:
        case LogLevel.CRITICAL:
            console.error(...args);
            break;
    }
}

/**
 * Console form of a named event.
 *
 * The caller decides whether to call this — it is gated on the console floor, unlike the telemetry
 * side, which is unconditional. See `PcpLoggerService`'s `trackEvent`.
 */
export function writeEventToConsole(name: string, properties?: Record<string, unknown>): void {
    // eslint-disable-next-line no-console
    console.info(`[event] ${name}`, properties ?? '');
}

/** Console form of a named metric. Gated by the caller, like {@link writeEventToConsole}. */
export function writeMetricToConsole(name: string, value: number, properties?: Record<string, unknown>): void {
    // eslint-disable-next-line no-console
    console.info(`[metric] ${name} = ${value}`, properties ?? '');
}
